<template>
  <card body-classes="px-0 pb-1" class="no-border-card" footer-classes="pb-2">
    <template slot="header">
      <h3 class="mb-0">Listado de Lotes</h3>
    </template>

    <div>
      <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
        <el-select v-model="pagination.perPage" class="select-primary pagination-select" placeholder="Per page">
          <el-option
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
            class="select-primary"
          ></el-option>
        </el-select>

        <div>
          <base-input v-model="mixSearchQuery" placeholder="Buscar..." prepend-icon="fas fa-search"> </base-input>
        </div>
      </div>
    </div>

    <div class="table-responsive">
      <el-table :data="queriedData" header-row-class-name="thead-light" row-key="id" @sort-change="sortChange">
        <el-table-column label="Siembra" min-width="140px">
          <template #default="{ row }">
            <router-link
              v-if="!row.isShare && $can('store', 'plot_permissions')"
              :to="{ name: 'PlotPermissions', params: { id: row.id } }"
              class="btn base-button btn-link btn-md mb-1 p-0 float-left"
              style="font-size: 16px"
            >
              <i class="fas fa-share-alt"></i>
            </router-link>

            <div class="d-flex justify-content-center">
              <small v-if="row.preEmergence">
                {{ row.preEmergence.seedDate | date }}
              </small>
              <small v-else class="font-italic">-</small>
            </div>

            <div class="d-block text-center">
              <span class="text-dark text-sm"> <i class="fas fa-images"></i> {{ row.picturesCount }} </span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Productor" min-width="130px" prop="field.producer">
          <template #default="{ row }">
            <div>
              <router-link
                v-if="$can('show', 'producers')"
                class="h5"
                :to="{ name: 'ProducerShow', params: { id: row.field.producer.id } }"
              >
                {{ row.field.producer.businessName }}
              </router-link>
              <p v-else>{{ row.field.producer.businessName }}</p>
            </div>
            <small>{{ row.name }} - {{ row.field.name }}</small>
          </template>
        </el-table-column>

        <el-table-column label="Distribuidor" min-width="140px" prop="field.producer.distributor">
          <template #default="{ row }">
            <div>
              <router-link
                v-if="$can('show', 'distributors')"
                class="h5"
                :to="{ name: 'DistributorShow', params: { id: row.field.producer.distributor.id } }"
              >
                {{ row.field.producer.distributor.name }}
              </router-link>
              <span v-else class="h5">{{ row.field.producer.distributor.name }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Variedad" min-width="135px" prop="variety_id" sortable>
          <template #default="{ row }">
            <div>{{ row.variety.name }}</div>
            <small>{{ row.campaign.name }}</small>
          </template>
        </el-table-column>

        <el-table-column label="HAS." min-width="100px" prop="hectares" sortable> </el-table-column>

        <el-table-column label="Rendimiento" min-width="140px">
          <template #default="{ row }">
            <div class="text-center">
              <div v-if="row.harvest && (row.harvest.storagePerformance || row.harvest.harvestPerformance)">
                <div v-if="row.harvest.storagePerformance">{{ row.harvest.storagePerformance }} kg/ha</div>
                <div v-if="row.harvest.harvestPerformance">{{ row.harvest.harvestPerformance }} mv/ha</div>
              </div>
              <div v-else>-</div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Etapa" min-width="135px" prop="preEmergence">
          <template #default="{ row }">
            <div class="text-left">
              <router-link :to="gotoFollowUp(row)">
                <badge v-if="row.preEmergence" class="follow-up-link mb-0" type="primary">
                  <i class="fas fa-calendar-alt mr-1"></i>
                  <small v-if="row.harvest">Producción</small>
                  <small v-else-if="row.emergence">Emergencia</small>
                  <small v-else-if="row.preEmergence">Pre-Emergencia</small>
                </badge>
                <badge v-else class="follow-up-link mb-0" type="danger">
                  <i class="fas fa-calendar-alt mr-1"></i>
                  <small>Sin seguimiento</small>
                </badge>
              </router-link>
            </div>
          </template>
        </el-table-column>

        <el-table-column align="right" label="" min-width="145px">
          <template slot-scope="{ row }">
            <div class="d-flex">
              <router-link
                v-if="(!row.isShare && $can('show', 'plots')) || (row.isShare && row.read)"
                :to="{ name: 'PlotShow', params: { id: row.id } }"
                title="Ver"
                class="btn base-button btn-outline-info btn-sm float-left"
              >
                <i class="fas fa-eye"></i>
              </router-link>
              <router-link
                v-if="(!row.isShare && $can('update', 'plots')) || (row.isShare && row.write)"
                :to="{ name: 'PlotEdit', params: { id: row.id } }"
                title="Editar"
                class="btn base-button btn-outline-primary btn-sm float-left"
              >
                <i class="fas fa-pen"></i>
              </router-link>
            </div>
            <div class="d-flex mt-2">
              <button
                v-if="!row.isShare && $can('destroy', 'plots')"
                title="Eliminar"
                class="btn base-button btn-outline-danger btn-sm"
                @click="deleteRow(row.id)"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div slot="footer" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
      <div>
        <p class="card-category">Mostrando {{ from + 1 }} a {{ to }} de un total de {{ pagination.total }} lotes</p>
      </div>
      <base-pagination
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :show-first-page-button="true"
        :show-last-page-button="true"
        :total="pagination.total"
        class="pagination-no-border"
        @input="changePage"
      >
      </base-pagination>
    </div>
  </card>
</template>

<script>
import { BasePagination } from '@/components';
import { Table, TableColumn, DropdownMenu, DropdownItem, Dropdown, Select, Option } from 'element-ui';
import PaginationOnServerMixin from '@/mixins/paginationOnServerMixin';
import DeleteRowFromTableMixin from '../../../mixins/deleteRowFromTableMixin';

export default {
  name: 'PlotsTable',
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    BasePagination,
  },

  mixins: [PaginationOnServerMixin, DeleteRowFromTableMixin],
  props: {
    filterCampaign: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      searchKeys: [
        'name',
        'hectares',
        'campaign.name',
        'variety.name',
        'field.name',
        'field.producer.businessName',
        'field.producer.distributor.name',
      ],

      tableData: [],
      modelName: 'plot',
    };
  },

  watch: {
    filterCampaign: {
      immediate: true,
      handler() {
        this.fetchTableData();
      },
    },
  },

  created() {
    this.defaultSort = '-id';
    this.resourceName = 'plots';
  },

  methods: {
    fetchTableData() {
      this.pagination.perPage = this.perPage || this.pagination.perPage;
      this.pagination.currentPage = this.page || this.pagination.currentPage;

      const params = {
        append: ['picturesCount'],
        include: [
          'campaign',
          'field.producer.distributor',
          'predecessor',
          'variety',
          'harvest',
          'emergence',
          'preEmergence',
        ],

        'filter[campaign]': this.filterCampaign,
        'filter[search]': this.mixSearchQuery,
        perPage: this.pagination.perPage,
        page: this.pagination.currentPage,
        sort: this.sort || this.defaultSort,
      };

      this.$services
        .resource('plots')
        .get(params)
        .then((res) => {
          this.setMeta(res.data.meta);
          return res.data.data;
        })
        .then((data) => (this.tableData = data.plots));
    },

    gotoFollowUp(plot) {
      if (!plot.isShare && this.$can('update', 'plots')) {
        return {
          name: 'PlotFollowUp',
          params: { id: plot.id },
          query: {
            stage: plot.harvest ? 3 : plot.emergence ? 2 : plot.preEmergence ? 1 : 0,
          },
        };
      }
      return {};
    },
  },
};
</script>

<style lang="scss" scoped>
div.el-table .el-table__header .cell,
::v-deep div.el-table .el-table__row .cell {
  word-break: keep-all;
}

.follow-up-link {
  &:hover {
    margin-bottom: 0.15rem !important;
    margin-left: 0.1rem !important;
  }

  &.badge-danger:hover {
    background: linear-gradient(rgba(248, 0, 49, 0.3), rgba(248, 0, 49, 0.3));
  }

  &.badge-primary:hover {
    background: linear-gradient(rgba(38, 67, 233, 0.3), rgba(38, 67, 233, 0.3));
  }
}
</style>
